import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight, Calendar, User, FileText } from 'lucide-react';
import { BlogAPI, resolveImage } from '../lib/api';
import { useSiteConfig } from '../context/SiteConfigContext';
import SafeHtml from '../components/SafeHtml';

export const BlogList = () => {
  const { config } = useSiteConfig();
  const cmsTitle = config['pages.blog_index.title'] || config['seo.blog.title'];
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { BlogAPI.list().then(setPosts).finally(() => setLoading(false)); }, []);

  return (
    <div className="bg-[#FAF7F2] min-h-screen">
      <div className="max-w-5xl mx-auto px-4 py-10">
        <nav className="flex items-center gap-1.5 text-sm text-[#7A6F5C] mb-4">
          <Link to="/" className="hover:text-[#1F4D3F]">Accueil</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-[#2D2A26]">Blog</span>
        </nav>
        <h1 className="font-serif text-3xl lg:text-4xl font-semibold text-[#1F4D3F] mb-2">{cmsTitle || 'Blog Comptoir-Online'}</h1>
        <p className="text-[#7A6F5C] mb-8">Actualités, conseils dentaires et nouveautés produits.</p>

        {loading ? (
          <div className="text-center text-[#7A6F5C] py-10">Chargement...</div>
        ) : posts.length === 0 ? (
          <div className="bg-white border border-[#E8E0D0] rounded-2xl p-10 text-center">
            <FileText className="w-12 h-12 mx-auto text-[#C9BFAE] mb-3" />
            <p className="text-[#7A6F5C]">Aucun article pour le moment. Revenez bientôt.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {posts.map((p) => (
              <Link to={`/blog/${p.slug}`} key={p.id} className="bg-white border border-[#E8E0D0] rounded-2xl overflow-hidden hover:shadow-md transition-shadow group" data-testid={`blog-card-${p.slug}`}>
                {p.cover_image ? (
                  <div className="aspect-[16/9] overflow-hidden bg-[#F5F1EA]">
                    <img src={resolveImage(p.cover_image)} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" onError={e => { e.target.style.visibility = 'hidden'; }} />
                  </div>
                ) : (
                  <div className="aspect-[16/9] bg-gradient-to-br from-[#1F4D3F] to-[#2D6452] flex items-center justify-center">
                    <FileText className="w-12 h-12 text-white/30" />
                  </div>
                )}
                <div className="p-5">
                  <h2 className="font-serif text-lg font-semibold text-[#1F4D3F] group-hover:text-[#173B30] line-clamp-2">{p.title}</h2>
                  {p.summary && <p className="text-sm text-[#5A5247] mt-2 line-clamp-3">{p.summary}</p>}
                  <div className="mt-3 flex items-center gap-3 text-xs text-[#7A6F5C]">
                    {p.author && <span className="inline-flex items-center gap-1"><User className="w-3 h-3" /> {p.author}</span>}
                    {p.created_at && <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(p.created_at).toLocaleDateString('fr-FR')}</span>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    BlogAPI.get(slug)
      .then(setPost)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="text-center py-20 text-[#7A6F5C]">Chargement...</div>;
  if (notFound || !post) return (
    <div className="bg-[#FAF7F2] min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h1 className="font-serif text-2xl text-[#1F4D3F]">Article introuvable</h1>
        <Link to="/blog" className="text-[#1F4D3F] underline mt-3 inline-block">Retour au blog</Link>
      </div>
    </div>
  );

  return (
    <div className="bg-[#FAF7F2] min-h-screen">
      <div className="max-w-3xl mx-auto px-4 py-10">
        <nav className="flex items-center gap-1.5 text-sm text-[#7A6F5C] mb-4">
          <Link to="/" className="hover:text-[#1F4D3F]">Accueil</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <Link to="/blog" className="hover:text-[#1F4D3F]">Blog</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-[#2D2A26] truncate max-w-[200px]">{post.title}</span>
        </nav>
        {post.cover_image && (
          <div className="aspect-[21/9] rounded-2xl overflow-hidden bg-[#F5F1EA] mb-6">
            <img src={resolveImage(post.cover_image)} alt={post.title} className="w-full h-full object-cover" onError={e => { e.target.style.visibility = 'hidden'; }} />
          </div>
        )}
        <h1 className="font-serif text-3xl lg:text-4xl font-semibold text-[#1F4D3F] mb-2">{post.title}</h1>
        <div className="flex items-center gap-3 text-xs text-[#7A6F5C] mb-6">
          {post.author && <span className="inline-flex items-center gap-1"><User className="w-3 h-3" /> {post.author}</span>}
          {post.created_at && <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(post.created_at).toLocaleDateString('fr-FR', { year:'numeric', month:'long', day:'numeric' })}</span>}
        </div>
        <article className="bg-white rounded-2xl border border-[#E8E0D0] p-6 lg:p-10 max-w-none text-[#2D2A26]">
          <SafeHtml html={post.body_html || ''} />
        </article>
      </div>
    </div>
  );
};
